import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Custom hook for managing message editing state
 * Handles edit mode, cursor position, keyboard shortcuts, and click-outside detection
 * 
 * @returns {Object} - Editing state and operations
 */
const useMessageEditing = () => {
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [originalEditingContent, setOriginalEditingContent] = useState('');
  
  const editTextareaRef = useRef(null);
  const editBubbleRef = useRef(null);
  const cursorPositionRef = useRef(null);
  const isInitialEditRef = useRef(false);

  /**
   * Start editing a message
   * 
   * @param {Object} message - Message to edit
   * @param {Event} event - Click event (optional)
   */
  const startEdit = useCallback((message, event) => {
    setEditingMessageId(message.id);
    setEditingContent(message.content);
    setOriginalEditingContent(message.content);
    isInitialEditRef.current = true;
  }, []);

  /**
   * Cancel editing and restore original content
   */
  const cancelEdit = useCallback(() => {
    setEditingMessageId(null);
    setEditingContent('');
    setOriginalEditingContent('');
    cursorPositionRef.current = null;
    isInitialEditRef.current = false;
  }, []);

  /**
   * Update editing content and track cursor position
   * 
   * @param {Event} e - Change event
   */
  const updateContent = useCallback((e) => {
    const textarea = e.target;
    const cursorPosition = textarea.selectionStart;
    const newValue = textarea.value;
    
    // Store cursor position before state update
    cursorPositionRef.current = cursorPosition;
    
    setEditingContent(newValue);
  }, []);

  // Auto-resize textarea and manage cursor position
  useEffect(() => {
    if (editingMessageId !== null && editTextareaRef.current) {
      const textarea = editTextareaRef.current;
      
      // Force a reflow to ensure accurate scrollHeight calculation
      textarea.style.height = '1px';
      // Add a small delay to ensure the DOM has updated
      requestAnimationFrame(() => {
        if (editTextareaRef.current) {
          const newHeight = Math.max(textarea.scrollHeight, 40); // Minimum 40px height
          textarea.style.height = `${newHeight}px`;
        }
      });
      
      // Handle cursor positioning
      if (isInitialEditRef.current) {
        // First time editing - position cursor at the end
        const length = textarea.value.length;
        textarea.setSelectionRange(length, length);
        textarea.focus();
        isInitialEditRef.current = false;
        cursorPositionRef.current = length;
      } else if (cursorPositionRef.current !== null) {
        // Restore cursor position after re-render
        const position = Math.min(cursorPositionRef.current, textarea.value.length);
        textarea.setSelectionRange(position, position);
      }
    }
  }, [editingMessageId, editingContent]);

  // Handle click outside and ESC key to cancel edit
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (editingMessageId !== null) {
        // Check if clicking on the submit button or inside it
        const target = event.target;
        const isSubmitButton = target.closest('[aria-label="Save & Continue"]');
        
        // If clicking submit button, don't cancel - let it handle submission
        if (isSubmitButton) {
          return;
        }
        
        // Small delay to ensure ref is set after re-render
        setTimeout(() => {
          if (editBubbleRef.current && !editBubbleRef.current.contains(event.target)) {
            cancelEdit();
          }
        }, 0);
      }
    };

    const handleEscKey = (event) => {
      if (event.key === 'Escape' && editingMessageId !== null) {
        event.preventDefault();
        event.stopPropagation();
        cancelEdit();
      }
    };

    if (editingMessageId !== null) {
      // Use capture phase for more reliable event handling
      document.addEventListener('mousedown', handleClickOutside, true);
      document.addEventListener('keydown', handleEscKey, true);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside, true);
        document.removeEventListener('keydown', handleEscKey, true);
      };
    }
  }, [editingMessageId, cancelEdit]);

  return {
    // State
    editingMessageId,
    editingContent,
    originalEditingContent,
    
    // Refs (expose for components)
    editTextareaRef,
    editBubbleRef,
    
    // Operations
    startEdit,
    cancelEdit,
    updateContent,
  };
};

export default useMessageEditing;

